"use client";

import type * as React from "react";
import {
  RemovableChip,
  RemovableChipButton,
} from "@/components/ui/atoms/removable-chip";
import { McpToolkitAvatar } from "./toolkit-avatar";

export type McpToolkitChipProps = {
  /** Toolkit display name — rendered with `capitalize` styling. */
  name: string;
  /** Branded-icon lookup key passed through to `McpToolkitAvatar`. */
  toolkitId?: string;
  /**
   * Total tools the toolkit exposes. Only rendered when `isPartial`, paired
   * with `selectedCount` to show the partial fraction.
   */
  total?: number;
  /** Selected tools from this toolkit. */
  selectedCount?: number;
  /**
   * The toolkit was added beyond the recommended set. Surfaced in the
   * aggregate "+N added" count rather than per-chip; here it only sets
   * `data-status`.
   */
  added?: boolean;
  /**
   * Some, but not all, tools selected — surfaces the `N/M` fraction and
   * sharpens the remove label so screen-reader users know how many tools
   * the action will drop.
   */
  isPartial?: boolean;
  /** Open this toolkit's tools to adjust the selection. */
  onOpen?: () => void;
  /** Remove the toolkit (and all of its selected tools). */
  onRemove?: () => void;
} & Omit<React.ComponentProps<"div">, "children">;

/**
 * Removable toolkit chip with two affordances: the body opens that server's
 * tools (to adjust the selection); the trailing × removes it. Built on
 * `RemovableChip`, which owns the shared shell, divider, and remove button.
 * Used inside the picker's selection summary bar.
 */
export function McpToolkitChip({
  name,
  toolkitId,
  total,
  selectedCount,
  added = false,
  isPartial = false,
  onOpen,
  onRemove,
  ...rest
}: McpToolkitChipProps) {
  const removeLabel =
    isPartial && selectedCount != null
      ? `Remove all ${selectedCount} selected ${name} tools`
      : `Remove ${name}`;
  const openLabel = `Open ${name} tools`;
  let status: "added" | "partial" | "selected" = "selected";
  if (added) {
    status = "added";
  } else if (isPartial) {
    status = "partial";
  }

  return (
    <RemovableChip
      {...rest}
      data-slot="mcp-toolkit-chip"
      data-status={status}
      onRemove={onRemove}
      removeLabel={removeLabel}
    >
      <RemovableChipButton
        aria-label={openLabel}
        onClick={onOpen}
        title={openLabel}
      >
        <McpToolkitAvatar name={name} size="sm" toolkitId={toolkitId} />
        <span className="max-w-44 truncate font-medium capitalize">{name}</span>
        {isPartial && selectedCount != null && total != null ? (
          <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
            {selectedCount}/{total}
          </span>
        ) : null}
      </RemovableChipButton>
    </RemovableChip>
  );
}
